import React, { useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { RootState } from '../../app/store';
import Button from '../../components/button/Button';
import HistoryRouting from '../../components/HistoryRouting';
import Spinner from '../../components/spinner/Spinner';
import { UploadCorporateCards, ProcessCorporateCards } from '../../features/partnerSlice';
import toast from 'react-hot-toast';
import { BsInfoCircle } from 'react-icons/bs';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import InfoTooltip from '../../components/infoTooltip';

const CorporateCards: React.FC = () => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState<string>('');
  const [showInstructions, setShowInstructions] = useState(true);
  const { loadingStates } = useAppSelector((state: RootState) => state.partner);

  const isLoading = loadingStates['UploadCorporateCardsState'] || false;

  const validateCsvFile = (file: File): Promise<string[]> => {
    const expectedHeaders = [
      'id_external',
      'phone',
      'email',
      'first_name',
      'last_name',
      'card_number',
      'expiry_month',
      'expiry_year',
      'card_nick_name',
    ];

    return new Promise((resolve, reject) => {

      const reader = new FileReader();

      reader.onload = event => {
        try {
          const text = event.target?.result as string;
          const errors: string[] = [];
          if (!text) {
            resolve(['Uploaded file is empty.']);
            return;
          }

          const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
          if (lines.length === 0) {
            resolve(['Uploaded file is empty.']);
            return;
          }

          const headerLine = lines[0];
          const headers = headerLine.split(',').map(h => h.trim());

          if (headers.length !== expectedHeaders.length) {
            resolve([
              `CSV must contain exactly these columns (in this order) with no extras: ${expectedHeaders.join(
                ', '
              )}.`,
            ]);
            return;
          }

          for (let i = 0; i < expectedHeaders.length; i++) {
            if (headers[i] !== expectedHeaders[i]) {
              resolve([
                `Invalid header in column ${i + 1}. Expected "${expectedHeaders[i]}", found "${
                  headers[i] || ''
                }".`,
              ]);
              return;
            }
          }

          // Basic row-level validation
          for (let rowIndex = 1; rowIndex < lines.length; rowIndex++) {
            const row = lines[rowIndex];
            const cols = row.split(',');
            if (cols.every(col => col === '')) {
              continue;
            }

            // allow last empty line
            if (cols.length === 1 && cols[0].trim() === '') continue;

            if (cols.length !== expectedHeaders.length) {
              errors.push(
                `Row ${rowIndex + 1} does not have the correct number of columns. Expected ${
                  expectedHeaders.length
                }, found ${cols.length}.`
              );
              continue;
            }

            const [
              id_external,
              phone,
              email,
              first_name,
              last_name,
              card_number,
              expiry_month,
              expiry_year,
              card_nick_name,
            ] = cols.map(c => c.trim());
            console.log("first_name", first_name);
            console.log("last_name", last_name);
            console.log("id_external", id_external);


            if (!phone && !email ) {
              errors.push(
                `Row ${rowIndex + 1}: at least one of id_external, phone, or email must be provided.`
              );
            }

            if (phone && !/^1\d{9,14}$/.test(phone.replace(/\D/g, ''))) {
              errors.push(
                `Row ${rowIndex + 1}: phone must start with country code 1 and contain digits only (no spaces or symbols).`
              );
            }

            if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
              errors.push(`Row ${rowIndex + 1}: email is not in a valid format.`);
            }

            if (card_number && !/^\d{16}$/.test(card_number)) {
              errors.push(
                `Row ${rowIndex + 1}: card_number must be a 16 digit number with no spaces or special characters.`
              );
            }

            if (expiry_month && !/^(0?[1-9]|1[0-2])$/.test(expiry_month)) {
              errors.push(
                `Row ${rowIndex + 1}: expiry_month must be a number between 01 and 12.`
              );
            }

            if (expiry_year && !/^\d{4}$/.test(expiry_year)) {
              errors.push(
                `Row ${rowIndex + 1}: expiry_year must be a 4 digit year (e.g., 2029).`
              );
            }

            if (!card_nick_name) {
              errors.push(
                `Row ${rowIndex + 1}: card_nick_name is required and should identify the card.`
              );
            }
          }

          resolve(errors);
        } catch (err: any) {
          reject(err);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read CSV file.'));
      };

      reader.readAsText(file);
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const templateUrl =
        'https://s3.us-east-1.amazonaws.com/assets.saversapp.com/partner-assets/template.csv';
      const response = await fetch(templateUrl);
      if (!response.ok) {
        toast.error('Failed to download template file', { duration: 8000 });
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'corporate_cards_template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error('Failed to download template: ' + error?.message, {
        duration: 8000,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file', { duration: 8000 });
        return;
      }

      const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB
      if (file.size > maxSizeInBytes) {
        toast.error('Maximum file size is 5MB', { duration: 8000 });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload', { duration: 8000 });
      return;
    }

    if (!isConsentChecked) {
      setConsentError(
        'Please confirm that you are authorized and agree to the Corporate Card Enrollment Consent.'
      );
      return;
    }

    // Clear error if consent is checked
    setConsentError('');

    // Validate CSV structure and data before uploading
    try {
      const validationErrors = await validateCsvFile(selectedFile);
      if (validationErrors && validationErrors.length > 0) {
        // Show up to first 5 errors to avoid spamming
        const maxToShow = 5;
        validationErrors.slice(0, maxToShow).forEach(message => {
          toast.error(message, { duration: 10000 });
        });

        if (validationErrors.length > maxToShow) {
          toast.error(
            `+${validationErrors.length - maxToShow} more error(s). Please fix the CSV file and try again.`,
            { duration: 10000 }
          );
        }
        return;
      }
    } catch (validationException: any) {
      toast.error(
        validationException?.message || 'Failed to validate CSV file. Please try again.',
        { duration: 10000 }
      );
      return;
    }

    try {
      // First request: Get presigned URL
      const uploadResult: any = await dispatch(
        UploadCorporateCards({ fileName: selectedFile.name })
      );

      if (uploadResult?.status === 200 || uploadResult?.status === 201) {
        // Get presignedUrl and fileKey from response
        const presignedUrl = uploadResult?.data?.presignedUrl;
        const fileKey = uploadResult?.data?.fileKey;

        if (!presignedUrl || !fileKey) {
          toast.error('Presigned URL or file key not found in response', {
            duration: 8000,
          });
          return;
        }

        // Upload file to S3 using presigned URL
        try {
          const fileResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: selectedFile,
            headers: {
              'Content-Type': 'text/csv',
            },
          });

          if (!fileResponse.ok) {
            toast.error('Failed to upload file to S3', { duration: 8000 });
            return;
          }

          // Second request: Process the file
          const processResult: any = await dispatch(
            ProcessCorporateCards({ fileKey })
          );

          if (processResult?.status === 200 || processResult?.status === 201) {
            toast.success('File uploaded and processed successfully');
            setSelectedFile(null);
            setIsConsentChecked(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          } else {
            toast.error(
              processResult?.response?.data?.message || 'File processing failed',
              { duration: 8000 }
            );
          }
        } catch (s3Error: any) {
          toast.error('Failed to upload file to S3: ' + s3Error?.message, {
            duration: 8000,
          });
        }
      } else {
        toast.error(
          uploadResult?.response?.data?.message || 'File upload failed',
          { duration: 8000 }
        );
      }
    } catch (error: any) {
      toast.error(error?.message || 'Upload failed', { duration: 8000 });
    }
  };

  return (
    <>
      <Toaster />
      <HistoryRouting history='Program Management/Corporate Cards' />
      <div className='justify-center max-w-full rounded-lg primary-shadow'>
        <div className='bg-white rounded-lg w-full'>
          <div className='mb-8'>
            <div className='mb-6 pb-4'>
              <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                Corporate Cards
              </h2>
              <div className='mb-0 p-4 md:p-8'>
              <div className='flex items-center mb-6 md:mb-8'>
              <p className='text-sm md:text-base text-[#343434]'>
                  <strong>Securely</strong> upload a CSV file containing corporate card information. Download the template file to see the required format.
                  
                </p>
                <InfoTooltip content={'The file is encrypted at rest using AES-256 (SSE-S3), transmitted over HTTPS (TLS 1.2+), and accessed via a pre-signed URL that is valid for 15 minutes only.'} className='ml-1'/>

              </div>

                {/* File input + consent text block (left) + buttons (right on xl) */}
                <div className='flex flex-col xl:flex-row gap-4 mb-4'>
                  <div className='w-full xl:flex-1'>
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='.csv'
                      onChange={handleFileChange}
                      className='hidden'
                      id='csv-upload'
                    />
                    <label
                      htmlFor='csv-upload'
                      className='block w-full'
                    >
                      <div className='flex items-center gap-0'>
                        <input
                          type='text'
                          value={selectedFile?.name || ''}
                          readOnly
                          placeholder='No file selected (CSV only)'
                          className='register-input flex-1 rounded-r-[0px] text-sm my-0'
                        />
                        <label
                          htmlFor='csv-upload'
                          className='text-sm cursor-pointer py-4 px-6 bg-[#aeaeae] text-white text-center hover:bg-[#9a9a9a] flex justify-center items-center rounded-r border border-l-0 border-gray-300 whitespace-nowrap font-semibold'
                        >
                          Choose File
                        </label>
                      </div>
                    </label>

                    {/* Consent checkbox - below file input */}
                    <div className='mt-4'>
                      <div className='flex items-center gap-2 text-[14px] text-[#343434]'>
                        <input
                          type='checkbox'
                          checked={isConsentChecked}
                          onChange={e => {
                            setIsConsentChecked(e.target.checked);
                            // Clear error when checkbox is checked
                            if (e.target.checked) {
                              setConsentError('');
                            }
                          }}
                          className='h-4 w-4 cursor-pointer flex-shrink-0'
                        />
                        <span className='cursor-default select-none'>
                          I confirm I'm authorized to enroll these corporate cards and I
                          agree to the{' '}
                          <a
                            href='/corp-card-consent'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-semibold text-primary hover:underline'
                            onClick={e => e.stopPropagation()}
                          >
                            Corporate Card Enrollment Consent
                          </a>
                          .
                        </span>
                      </div>
                      {consentError && (
                        <p className='text-red-600 text-xs md:text-sm font-semibold mt-2 ml-6'>
                          {consentError}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action buttons column */}
                  <div className='flex flex-col sm:flex-row gap-2 xl:items-start h-14'>
                    <Button
                      onClick={handleUpload}
                      className='w-full sm:w-auto bg-[#00C389] text-white px-6 py-3 rounded-lg h-full hover:bg-[#00b37a] transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold'
                      disabled={!selectedFile || isLoading}
                    >
                      {isLoading ? (
                        <span className='flex items-center justify-center gap-2'>
                          <Spinner />
                          Uploading...
                        </span>
                      ) : (
                        'Upload'
                      )}
                    </Button>
                    <Button
                      onClick={handleDownloadTemplate}
                      className='w-full sm:w-auto bg-primary text-white px-6 py-3  h-full rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold'
                      disabled={isLoading}
                    >
                      Download Template
                    </Button>
                  </div>
                </div>

                {/* Consent Description full width under both columns */}
                <div className='mb-6 text-xs text-gray-600 leading-relaxed mt-14 sm:mt-0'>
                  <p>
                    By checking this box and uploading, you confirm you are an authorized
                    representative of the Program and you authorize the applicable payment
                    card network(s) to monitor the Corporate Cards in your file and share
                    purchase data as required to participate in the Program, in accordance
                    with the{' '}
                    <a
                      href='https://members.saversapp.com/terms-and-conditions'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary hover:underline font-semibold'
                    >
                      Program Terms
                    </a>{' '}
                    and{' '}
                    <a
                      href='https://members.saversapp.com/privacy-policy'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary hover:underline font-semibold'
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                  <p>
                    You understand that Corporate Card purchase data (including date/time,
                    purchase amount, and merchant name, location, category) will be shared
                    with Savers App, and participating merchant partners funding rewards
                    to enable card-linked offers, calculate rewards, provide reward-status
                    notifications, and produce corporate reporting. This authorization
                    remains in effect until revoked by unenrolling/removing the Corporate
                    Card(s) through the Program Portal or other Program processes.
                  </p>
                </div>

                {/* Instructions Section */}
                <div className='mt-8 border border-gray-200 rounded-lg overflow-hidden'>
                  <button
                    type='button'
                    onClick={() => setShowInstructions(!showInstructions)}
                    className='w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors'
                  >
                    <div className='flex items-center gap-2'>
                      <BsInfoCircle className='text-primary text-lg' />
                      <span className='text-sm md:text-base font-semibold text-gray-700'>
                        CSV File Format Instructions
                      </span>
                    </div>
                    {showInstructions ? (
                      <MdExpandLess className='text-gray-600 text-xl' />
                    ) : (
                      <MdExpandMore className='text-gray-600 text-xl' />
                    )}
                  </button>

                  {showInstructions && (
                    <div className='p-4 md:p-6 bg-white border-t border-gray-200'>
                      <div className='space-y-4'>
                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            id_external
                          </h4>
                          <p className='text-sm text-gray-600'>
                            This field is any ID that you are currently using to identify your members and is only required for your own reference. If your members are being identified by either their phone number or email address, then this field can be left blank.
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            phone
                          </h4>
                          <p className='text-sm text-gray-600'>
                            This should start with the country code (1) and not include any spaces or other characters.
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            email
                          </h4>
                          <p className='text-sm text-gray-600'>
                            Member's email address.
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            first_name
                          </h4>
                          <p className='text-sm text-gray-600'>
                            This can be any name your members use, and does not have to be the same as the name on the card.
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            last_name
                          </h4>
                          <p className='text-sm text-gray-600'>
                            This can be any name your members use, and does not have to be the same as the name on the card.
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            card_number
                          </h4>
                          <p className='text-sm text-gray-600'>
                            16 digit card number without any spaces or special characters.
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            expiry_month
                          </h4>
                          <p className='text-sm text-gray-600'>
                            Expiry month in number format (e.g., 01, 12).
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            expiry_year
                          </h4>
                          <p className='text-sm text-gray-600'>
                            Expiry year in 4 digit number format (e.g., 2029).
                          </p>
                        </div>

                        <div>
                          <h4 className='font-semibold text-sm md:text-base text-gray-800 mb-2'>
                            card_nick_name
                          </h4>
                          <p className='text-sm text-gray-600'>
                            Any name that you can identify the card with.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CorporateCards;
